import {
  Component,
  DestroyRef,
  ElementRef,
  inject,
  input,
  OnInit,
  output,
  TemplateRef,
  viewChild
} from '@angular/core';
import { EmojiComponent } from '@ctrl/ngx-emoji-mart/ngx-emoji';
import { HtmlInputDirective } from '../../../directives/sb-client-message-input/html-input.directive';
import { MatIcon } from '@angular/material/icon';
import { MatButton, MatMiniFabButton } from '@angular/material/button';
import { AsyncPipe, DatePipe, NgClass } from '@angular/common';
import { EventStatus, MatrixEvent, MsgType } from 'matrix-js-sdk';
import { ReactionsService } from '../../../services/chat-message/reactions/reactions.service';
import { DisplayMessage } from '../sb-model/display-message';
import { MatCard, MatCardContent, MatCardHeader, MatCardSubtitle } from '@angular/material/card';
import { MatTooltip } from '@angular/material/tooltip';
import { ReactionDto } from '../sb-model/reaction-dto';
import { MatDialog, MatDialogActions, MatDialogContent, MatDialogTitle } from '@angular/material/dialog';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { HighlightPipe } from '../../../common/pipes/highlight.pipe';
import { SnackbarService } from '../../../services/snackbar/snackbar.service';
import { Room } from '../../../../shared-api/room';
import { UserIconComponent } from '../user-icon/user-icon.component';
import { first, from } from 'rxjs';
import { AccountService } from '../../../services/account/account.service';
import { DisplayMessageFile } from '../sb-model/display-message-file';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { PinnedMessageService } from '../../../services/pinMessages/pinned-message.service';
import { SpoilerComponent } from '../sb-spoiler/sb-spoiler.component';
import { SpoilerChunksPipe } from '../../../common/pipes/spoilerChuncks.pipe';
import { DateUtils } from '../../../services/util';
import { MediaEventContent } from 'matrix-js-sdk/lib/@types/media';
import { MediaService } from '../../../services/media/media.service';
import { FileService } from '../../../services/file/file.service';
import { MatrixService } from '../../../services/matrix/matrix.service';
import { HistoryEntry } from '../../../model/history-entry';
import { HistoryService } from '../../../services/history/history.service';
import { SbUserProfileCardComponent } from '../sb-user-profile-card/sb-user-profile-card.component';
import { MessageService } from '../../../services/message/message.service';
import { PresenceService } from '../../../services/presence/presence.service';
import { KnownMembership } from 'matrix-js-sdk/lib/@types/membership.js';
import { ButtonComponent } from '../../../common/components/button/button.component';
import { RoomHelperService } from '../../../services/room/room-helper.service';
import { RoomListAccessService } from '../../../services/room-list/room-list-access.service';
import DOMPurify from 'dompurify';
import { SbMessageTestIds } from './sb-message.test-ids';
import { CleanReplyPipe } from '../../../common/pipes/clean-reply.pipe';

@Component({
  selector: 'app-sb-message',
  imports: [
    EmojiComponent,
    HtmlInputDirective,
    MatIcon,
    MatMiniFabButton,
    MatCardContent,
    DatePipe,
    MatCard,
    MatCardHeader,
    MatTooltip,
    MatDialogTitle,
    MatDialogContent,
    UserIconComponent,
    MatButton,
    SpoilerComponent,
    SpoilerChunksPipe,
    MatCardSubtitle,
    AsyncPipe,
    MatDialogActions,
    ButtonComponent,
    NgClass,
    CleanReplyPipe
  ],
  providers: [HighlightPipe],
  templateUrl: './sb-message.component.html',
  styleUrl: './sb-message.component.scss'
})
/**
 * Diese Komponente stellt eine einzelne Nachricht innerhalb eines Matrix-Chats dar.
 * Sie ermöglicht das Hinzufügen von Reaktionen (Emojis) und deren Verwaltung über den
 * {@link ReactionsService}.
 *
 * Funktionen:
 * - Anzeige einer Nachricht.
 * - Möglichkeit, Emojis als Reaktion zu setzen oder zu entfernen.
 * - Verwaltung der Reaktions-Buttons und visuelles Feedback.
 * - Prüfung der Unicode-Länge von Reaktionen gemäß Spezifikation A_26227-01.
 * - Erneutes Senden einer Nachricht
 *
 * Hierdurch erfüllte AFO`s
 * - A_25395-01 - Matrix Module (Event Annotations and Reactions)
 *   - Fähigkeit zur Verarbeitung und Anzeige.
 *   - Fähigkeit zur Erzeugung.
 * - A_26227-01 - Längenbegrenzung von Annotationen in Reactions
 * - A_25423 - Retry and Order
 * - A_25524 - Anzeigen von Metadaten
 * - A_28146 - Empfang und Darstellung aller Matrix msgtypes
 */
export class SbMessageComponent implements OnInit {
  public room = input.required<Room>();
  public message = input<DisplayMessage>();
  public messageFile = input<DisplayMessageFile>();
  public isNewMessage = input<boolean>();
  public fileUrl: string | undefined;
  historyEntries: HistoryEntry[] = [];
  public currentFileStatus: {
    status: 'active' | 'inactive';
    fileName: string;
  } = {
    status: 'inactive',
    fileName: this.messageFile()?.name ?? ''
  };
  readonly canvasRef = viewChild.required<ElementRef<HTMLCanvasElement>>('canvas');
  protected userId: string | undefined;
  protected showPin = false;
  protected readonly testIds = SbMessageTestIds;
  protected readonly reactionService = inject(ReactionsService);
  protected readonly accountService = inject(AccountService);
  protected toolbarMessageId = output<string>();
  protected readonly EventStatus = EventStatus;
  protected readonly MsgType = MsgType;
  protected blurhashImage = '';
  protected readonly clientService = inject(MatrixService);
  protected readonly Object = Object;
  protected readonly roomHelperService = inject(RoomHelperService);
  protected readonly presenceService = inject(PresenceService);
  protected readonly pinnedMessageService = inject(PinnedMessageService);
  protected readonly destroyRef = inject(DestroyRef);
  protected loadMetadataText = 'Metadaten werden geladen...';
  protected readonly DateUtils = DateUtils;
  protected readonly KnownMembership = KnownMembership;
  private readonly roomListAccessService = inject(RoomListAccessService);
  private readonly errorImages = new Array<string>();
  private readonly messageService = inject(MessageService);
  private readonly dialog = inject(MatDialog);
  private readonly domSanitizer = inject(DomSanitizer);
  private readonly highlightPipe = inject(HighlightPipe);
  private readonly mediaService = inject(MediaService);
  private readonly fileService = inject(FileService);
  private readonly snackbarService = inject(SnackbarService);
  private readonly historyService = inject(HistoryService);
  private readonly FILE_ICON_MAP: Record<string, string> = {
    doc: 'word',
    docx: 'word',
    txt: 'file',
    mp4: 'play',
    pdf: 'file-pdf'
  };

  async checkReaction(
    reactionsArray: ReactionDto[],
    emoji: string,
    events: MatrixEvent[],
    messageEventId: string | undefined
  ): Promise<void> {
    if (this.room()) {
      try {
        await this.reactionService.checkReaction(reactionsArray, emoji, events, messageEventId, this.room());
      } catch (e) {
        console.warn(e);
        this.snackbarService.error('Fehler beim Senden der Reaktion');
      }
    }
  }

  public isSentByMe(message: DisplayMessage): boolean {
    return message.senderId === this.accountService.getMyMxId();
  }

  /** Überprüft, ob der aktuell eingeloggte Nutzer die Reaktion hatte **/
  public isOwnReaction(reaction: ReactionDto): boolean {
    if (!reaction) return false;
    return reaction.events.map(event => event.getSender()).includes(this.accountService.getMyMxId());
  }

  public getIdFromUnicode(unicode: string): string {
    return this.reactionService.getIdFromUnicode(unicode);
  }

  public toogleToolbarAndPin(message: DisplayMessage | undefined): void {
    this.toolbarMessageId.emit(message?.id ?? '');
    if (message?.content['msgtype'] !== 'm.bad.encrypted') {
      this.showPin = true;
    }
  }

  onMouseLeave(event: MouseEvent): void {
    const target = event.relatedTarget as HTMLElement | null;
    // Toolbar offen lassen, wenn die Maus zum CDK-Overlay (mat-menu, Reaction-Picker etc.)
    // oder zum Reaction-Button wandert — sonst schließt sich das Menü sofort.
    if (target && !target.closest('.add-reaction-button') && !target.closest('.cdk-overlay-container')) {
      this.toogleToolbarAndPin(undefined);
    }
    this.showPin = false;
  }

  /**
   * Bei nicht erfolgreichem senden wird hier das erneute Senden einer Nachricht angestoßen.
   * Erfüllt die Anforderung A_25423.
   * @param message Die zu sendende Nachricht
   */
  public sendAgain(message: DisplayMessage | undefined): void {
    if (message?.mentionsArray) {
      const mentionsArray = [...message.mentionsArray];
      this.messageService.removeEvent(this.room()?.roomId, message.id);
      from(
        this.messageService.sendCustomMessage(this.room()?.roomId, message.body, message.formattedBody, mentionsArray)
      )
        .pipe(first(), takeUntilDestroyed(this.destroyRef))
        .subscribe();
    }
  }

  async downloadFile(messageFile: DisplayMessageFile): Promise<void> {
    const confirmed = await this.mediaService.handleDownloadDialog();
    if (!confirmed) {
      return;
    }
    if (this.currentFileStatus?.status === 'inactive') {
      this.snackbarService.error('Dieser Dateityp ist nicht zugelassen und kann nicht heruntergeladen werden');
      return;
    }
    if (!this.fileService.isAllowedMimeType(messageFile.mimetype ?? '')) {
      this.snackbarService.error('Dateityp nicht erlaubt');
      return;
    }
    const response = await this.mediaService.decryptMessageFile(messageFile);
    if (!response.isValid) {
      this.snackbarService.error('Dies ist keine sichere Datei und kann daher nicht runtergeladen werden');
      return;
    }
    if (response.blob && response.blob.size > 0) {
      this.fileUrl = URL.createObjectURL(response.blob);
      const link = document.createElement('a');
      link.href = this.fileUrl;
      link.download = messageFile.name;
      document.body.appendChild(link);
      link.click();
      link.remove();
    }
  }

  /**
   * Metadaten werden durch fetchMetadataOnly geholt und in dieser Methode am Tooltip gesetzt
   * Erfüllt die Afo A_25524 - Anzeige von Metadaten
   * @param messageFile
   */
  async showMetadata(messageFile: DisplayMessageFile): Promise<void> {
    const file: Partial<MediaEventContent> = messageFile.content['file'];
    if (!file.url) {
      this.loadMetadataText = 'Metadaten konnten nicht geladen werden.';
      return;
    }
    const headers = await this.mediaService.fetchMetadataOnly(file.url);
    const metadata: Record<string, string> = {};
    headers.forEach((value, key) => {
      metadata[key] = value;
    });
    this.loadMetadataText = this.formatMetadata(metadata);
  }

  formatMetadata(metadata: Record<string, string>): string {
    return Object.entries(metadata)
      .map(([key, value]) => `${key}: ${value}`)
      .join('\n');
  }

  handleError(messageFile: DisplayMessageFile, blurhash: string): void {
    if (messageFile) {
      this.errorImages.push(messageFile.content['info']['tim_messenger.blurhash']);
      messageFile.url = blurhash;
    }
  }

  onKeyDown(event: KeyboardEvent, messageFile: DisplayMessageFile): void {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.downloadFile(messageFile);
    }
  }

  onImageLoad(messageFile: DisplayMessageFile): void {
    messageFile.isLoaded = true;
  }

  /**
   * A_26249 - Historie von Event Replacements
   * @param historyDialogRef
   * @param relationId
   */
  openHistory(historyDialogRef: TemplateRef<MatDialog>, relationId: string | undefined): void {
    if (relationId) {
      this.historyEntries = this.historyService.getHistory(relationId);
      this.dialog.open(historyDialogRef);
    }
  }

  /**
   * A_26261 - Eindeutige Darstellung von Event Replacements
   * @param historyEntry
   */
  getSanatizedHighlighter(historyEntry: { originalContent: string; newContent: string; timestamp: string }): SafeHtml {
    const senderId = this.message()?.senderId || '';
    const author = senderId ? this.getName(senderId) : '';

    const transformed = this.highlightPipe.transform(historyEntry.originalContent, historyEntry.newContent, author);

    // Sanitize den resultierenden HTML-Inhalt mit DOMPurify
    const sanitizedHtml = DOMPurify.sanitize(transformed);

    // Verwende sanitized HTML und gebe es als sicheren HTML-Inhalt zurück
    return this.domSanitizer.bypassSecurityTrustHtml(sanitizedHtml);
  }

  ngOnInit(): void {
    this.userId = this.accountService.getMyMxId() ?? '';
    setTimeout(() => {
      const filename = this.messageFile()?.name;
      const mimeType = this.messageFile()?.mimetype;
      this.currentFileStatus = {
        status: 'inactive',
        fileName: filename ?? ''
      };
      if (filename && mimeType) {
        const isValid = this.mediaService.validateDecryptedFileMetadata(mimeType, filename);
        this.currentFileStatus = { status: isValid ? 'active' : 'inactive', fileName: filename };
      }
      const content = this.messageFile()?.['content']?.['info'];
      const thumbnail = content?.['thumbnail_info'];
      const blurhash = content?.['tim_messenger.blurhash'];
      const width = thumbnail?.['w'];
      const height = thumbnail?.['h'];
      if (blurhash && width && height) {
        this.blurhashImage = this.mediaService.decodeBlurhash(this.canvasRef().nativeElement, blurhash, width, height);
      }
    }, 500);
  }

  getFileTypeIcon(fileName?: string): string | null {
    if (!fileName) return null;
    const extension = fileName.split('.').pop()?.toLowerCase();
    return this.FILE_ICON_MAP[extension!] || null;
  }

  getFileIcon(fileName: string, fileSize?: number): string {
    if (!fileName || !fileSize) return 'file-fail';
    const isValid = this.currentFileStatus?.status === 'active';
    return isValid ? 'file-success' : 'file-fail';
  }

  scrollToMessage(eventId: string | undefined): void {
    if (!eventId) return;
    const element = document.getElementById(eventId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  formatFileSize(sizeInBytes: number): string {
    if (sizeInBytes === 0) return '0B';
    const units = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(sizeInBytes) / Math.log(1024));
    return `${(sizeInBytes / Math.pow(1024, i)).toFixed(1)} ${units[i]}`;
  }

  getMessageText(message: DisplayMessage): string {
    return message?.content?.['org.matrix.msc1767.text'] || message?.content?.['body'] || '';
  }

  handleMention(event: MouseEvent): void {
    const target = event.target as HTMLAnchorElement;

    event.preventDefault();
    event.stopPropagation();
    this.dialog.closeAll();

    const href = target.href;
    if (!href?.includes('matrix.to')) {
      return;
    }

    const parts = href.split('#/');
    if (parts.length < 2) {
      return;
    }

    const userId = parts[1];

    const member = this.roomListAccessService.getModelRoomMember(this.room().roomId, userId);

    if (!member) {
      return;
    }

    const dialogRef = this.dialog.open(SbUserProfileCardComponent, {
      panelClass: 'user-profile-dialog',
      data: { member }
    });

    dialogRef.componentInstance.closed.subscribe(() => {
      dialogRef.close();
    });
  }

  onCancel(): void {
    this.dialog.closeAll();
  }

  private getName(userId: string): string {
    return userId.slice(1, userId.indexOf(':'));
  }
}
